// Full courses table, drilled to from the All Courses card's "View all" on
// the Courses page. Every course in scope aggregated from the send log with
// sends, completed and outstanding counts; sortable, paginated and exportable
// via the shared DataTable. Row click filters the Courses page to the course.
import React, { useMemo } from 'react';
import { motion } from 'motion/react';
import { COURSE_SEND_LOG, courseType } from '../data';
import useDashboardData, { logInPeriod } from '../hooks/useDashboardData';
import { toServerScope, userScopeIsEmails } from '../serverScope';
import ActiveFilters from './ActiveFilters';
import DataTable from './DataTable';

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

const isIn = (val, vs) => { if (!vs) return true; const a = Array.isArray(val) ? val : [val]; return Array.isArray(vs) ? a.some((x) => vs.includes(x)) : a.includes(vs); };

// Per-course sends, completed and outstanding counts over a send log. Shared
// with the All Courses card on CoursesPage so the two always agree.
export function buildCourseRows(log) {
    const byCourse = new Map();
    for (const e of log || []) {
        const cur = byCourse.get(e.courseName) || { name: e.courseName, type: e.courseType, sent: 0, completed: 0 };
        cur.sent += 1;
        if (e.dateCompleted) cur.completed += 1;
        byCourse.set(e.courseName, cur);
    }
    return [...byCourse.values()].map((c) => ({
        ...c,
        outstanding: c.sent - c.completed,
        completionPct: c.sent ? Math.round((c.completed / c.sent) * 100) : 0,
    }));
}

export function courseRowsFromAgg(rows) {
    return (rows || []).filter((r) => (r.sent || 0) > 0).map((r) => ({
        name: r.courseName,
        type: courseType(r.courseName),
        sent: r.sent || 0,
        completed: r.completed || 0,
        outstanding: (r.sent || 0) - (r.completed || 0),
        completionPct: r.sent ? Math.round(((r.completed || 0) / r.sent) * 100) : 0,
    }));
}

function scopedCourseRows(scope, range) {
    const s = scope || {};
    return buildCourseRows(logInPeriod(COURSE_SEND_LOG, range).filter((e) => {
        if (!isIn(e.dept,       s.department)) return false;
        if (!isIn(e.location,   s.location))   return false;
        if (!isIn(e.city,       s.city))       return false;
        if (!isIn(e.country,    s.country))    return false;
        if (!isIn(e.company,    s.company))    return false;
        if (!isIn(e.state,      s.state))      return false;
        if (!isIn(e.adGroups,    s.adGroup))    return false;
        if (!isIn(e.courseName, s.course))     return false;
        if (s.user) {
            const vs = Array.isArray(s.user) ? s.user : [s.user];
            if (!vs.some((v) => e.email === v || `${e.firstName} ${e.lastName}` === v)) return false;
        }
        return true;
    }));
}

export default function AllCoursesPage({ drill, scope, removeScope, period, startDate, endDate }) {
    const emailsOnly = userScopeIsEmails(scope);
    const coursesQ = useDashboardData({ metric: 'courses', aggregation: 'courses', period, startDate, endDate, scope: toServerScope(scope) }, { skip: !emailsOnly });
    const aggRows = (emailsOnly && Array.isArray(coursesQ.data?.courses)) ? coursesQ.data.courses : null;
    const rows = useMemo(() => {
        if (!aggRows) return scopedCourseRows(scope, { period, startDate, endDate });
        const courseVals = scope?.course ? [].concat(scope.course) : null;
        return courseRowsFromAgg(aggRows).filter((c) => !courseVals || courseVals.includes(c.name));
    }, [aggRows, scope, period, startDate, endDate]);

    const columns = [
        { id: 'name',          label: 'Course',       always: true,    sortable: true, type: 'string', accessor: (c) => c.name },
        { id: 'type',          label: 'Course Type',  defaultOn: true, sortable: true, type: 'string', accessor: (c) => c.type || '' },
        { id: 'sent',          label: 'Sends',        defaultOn: true, sortable: true, type: 'number', accessor: (c) => c.sent, align: 'right' },
        { id: 'completed',     label: 'Completed',    defaultOn: true, sortable: true, type: 'number', accessor: (c) => c.completed, align: 'right' },
        { id: 'outstanding',   label: 'Outstanding',  defaultOn: true, sortable: true, type: 'number', accessor: (c) => c.outstanding, align: 'right' },
        { id: 'completionPct', label: 'Completion %', defaultOn: true, sortable: true, type: 'number', accessor: (c) => c.completionPct, align: 'right',
          format: (v) => `${v}%` },
    ];

    return (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ padding: '0 24px 48px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <ActiveFilters scope={scope} removeScope={removeScope} noPadX />
            <motion.div variants={fadeUp}>
                <DataTable
                    title="All Courses"
                    columns={columns}
                    rows={rows}
                    rowKey={(c) => c.name}
                    onRowClick={(c) => drill('course-detail', c.name, { type: 'course', value: c.name })}
                    initialSort={{ by: 'sent', dir: 'desc' }}
                    storageKey="rbx-all-courses-cols"
                    emptyMessage="No courses in scope"
                />
            </motion.div>
        </motion.div>
    );
}
